import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';

import { Grid, Box, Card, CardContent, Typography, Divider, Slider } from '@mui/material';

import { reportPageStyles as styles } from '../styles';
import { futureProjections } from '../../../configs/FinancialKPIConfig';
import { COLOR_PRIMARY, exchangeRates } from 'src/utils/const';
import { DropdownSelect } from 'src/App/Dashboards/Market/Components/Inputs';
import { COLOR_GREY_400 } from 'src/utils/colors';
import { abbreviateNumber, isObject } from 'src/utils/functions';
import DisabledBlurCard from '../../../UtilityComponents/BlurCard';
import { useSelector } from 'react-redux';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';
import MainKPITooltip from 'src/App/Dashboards/Market/Components/Tooltips/MainKPITooltip';
import TooltipContent from './CalculatorKPITooltip';
import { StartupCostSetupLink } from './InvestmentMetricsHighlightBox';

const renderChart = (
  graphKey,
  kpiData,
  currency,
  handleLoginOrPricingModalOpen,
  isUserSubscribed
) => {
  const graphConfig = futureProjections[graphKey];
  const { key, unit, legendMap, barColorsMap } = graphConfig;
  const chartData = kpiData[key];
  const isBreakevenAnalysis = graphKey === 'twentyFourMonthsBreakevenAnalysis';

  if (!chartData) return null;

  // For breakeven analysis, transform data to show only breakdown value as single bar.
  // Backend returns breakeven_value; fallback to total_net_operating_income for older reports.
  const formattedData = isBreakevenAnalysis
    ? Object.entries(chartData).map(([xValue, yValues]) => {
        const obj = typeof yValues === 'object' && yValues !== null ? (yValues as Record<string, number>) : {};
        const breakdownValue = obj.breakeven_value ?? obj.total_net_operating_income ?? 0;
        return {
          name: xValue,
          value: breakdownValue,
        };
      })
    : Object.entries(chartData).map(([xValue, yValues]) => ({
    name: xValue,
    ...(typeof yValues === 'object' ? yValues : { value: yValues }),
  }));

  // Calculate YAxis domain for breakeven analysis to include negative values
  let yAxisDomain: [number, number] | undefined = undefined;
  if (isBreakevenAnalysis && formattedData.length > 0) {
    const allValues = formattedData
      .map(d => (d as { name: string; value: number }).value)
      .filter(v => typeof v === 'number' && !isNaN(v));
    if (allValues.length > 0) {
      const minValue = Math.min(...allValues, 0);
      const maxValue = Math.max(...allValues, 0);
      const padding = Math.max(Math.abs(minValue), Math.abs(maxValue)) * 0.1;
      yAxisDomain = [minValue - padding, maxValue + padding];
    }
  }

  const CustomTooltip = props => {
    const { active, payload, label, currency, graphKey: tooltipGraphKey, kpiData: tooltipKpiData } = props;
    const isBreakevenAnalysis = tooltipGraphKey === 'twentyFourMonthsBreakevenAnalysis';

    if (active && payload && payload.length) {
      // Custom tooltip for breakeven analysis
      if (isBreakevenAnalysis) {
        const startupCosts = tooltipKpiData?.startup_costs_total || 0;
        // For breakeven analysis, the dataKey is 'value' since we transformed it to single bar
        const breakdownEntry = payload.find(entry => entry.dataKey === 'value' || entry.dataKey === 'total_net_operating_income');
        const breakdownValue = breakdownEntry?.value || 0;
        
        // Calculate cumulative net profit before tax
        // breakdown_value = -(startup_costs - cumulative_net_profit_before_tax)
        // So: cumulative_net_profit_before_tax = startup_costs + breakdown_value
        const cumulativeNetProfit = startupCosts + breakdownValue;
        
        // Amount away from breakeven (positive if negative breakdown, 0 if positive)
        const breakevenValue = breakdownValue < 0 ? Math.abs(breakdownValue) : 0;
        const hasReachedBreakeven = breakdownValue >= 0;
        const hasRecoveredSome = cumulativeNetProfit > 0;
        
        // Extract period number from label (e.g., "3 months" -> "3rd", "6 months" -> "6th")
        const periodMatch = label.match(/(\d+)\s+months?/i);
        const periodNumber = periodMatch ? parseInt(periodMatch[1]) : null;
        const periodOrdinal = periodNumber 
          ? periodNumber === 3 ? '3rd' 
            : periodNumber === 6 ? '6th'
            : periodNumber === 12 ? '12th'
            : periodNumber === 24 ? '24th'
            : `${periodNumber}th`
          : 'this period';

        const formatCurrencyValue = (value) => {
          return exchangeRates[currency].symbol + abbreviateNumber(Math.abs(value), true);
        };

        // Short, scannable tooltip: formula line + status. Formula: Net profit (first N months) − startup cost
        const formulaLine =
          periodNumber != null
            ? `Formula: Net profit (first ${periodNumber} months) − startup cost`
            : null;

        // Build tooltip message based on recovery status
        const getRecoveryMessage = () => {
          if (hasReachedBreakeven) {
            const profitAboveBreakeven = breakdownValue;
            return formulaLine
              ? `Breakeven reached. Current profit: ${formatCurrencyValue(profitAboveBreakeven)}\n\n${formulaLine}`
              : `Breakeven reached. Current profit: ${formatCurrencyValue(profitAboveBreakeven)}`;
          }

          return formulaLine
            ? `Recovered so far: ${formatCurrencyValue(cumulativeNetProfit)}\nRemaining to breakeven: ${formatCurrencyValue(breakevenValue)}\n\n${formulaLine}`
            : `Recovered so far: ${formatCurrencyValue(cumulativeNetProfit)}\nRemaining to breakeven: ${formatCurrencyValue(breakevenValue)}`;
        };

        return (
          <Card
            style={{
              backgroundColor: '#ffffff',
              padding: '15px',
              borderRadius: '8px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              maxWidth: '380px',
            }}
          >
            <CardContent>
              <Typography
                variant="body2"
                style={{ fontWeight: 600, marginBottom: '12px', lineHeight: 1.6, whiteSpace: 'pre-line' }}
              >
                {`Your initial startup investment was ${formatCurrencyValue(startupCosts)}.\n\n${getRecoveryMessage()}`}
              </Typography>
            </CardContent>
          </Card>
        );
      }

      // Default tooltip for other charts
      return (
        <Card
          style={{
            backgroundColor: '#ffffff',
            padding: '10px',
            borderRadius: '8px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          }}
        >
          <CardContent>
            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: '10px' }}>
              {label}
            </Typography>

            <Divider style={{ background: '#ccc', marginTop: 10, marginBottom: 10 }} />

            {payload.map((entry, index) => (
              <Typography
                key={`tooltip-${index}`}
                variant="body2"
                style={{
                  color: entry.color || '#000',
                  fontWeight: 500,
                  marginBottom: '5px',
                }}
              >
                {entry.name}:{' '}
                {entry.unit != '%'
                  ? entry.unit + abbreviateNumber(entry.value, true)
                  : abbreviateNumber(entry.value, true)}{' '}
                {entry.unit == '%' ? entry.unit : ''}
              </Typography>
            ))}
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  return (
    <Box key={key} sx={{ position: 'relative' }}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={formattedData}
          maxBarSize={20}
          style={{
            fontFamily: 'Roboto',
            position: 'relative',
          }}
        >
          <CartesianGrid
            strokeDasharray="6 6"
            stroke={COLOR_GREY_400}
            vertical={false}
            horizontal={true}
          />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            textAnchor="middle"
            style={{
              fontSize: '0.75rem',
              fontFamily: 'Roboto',
            }}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            domain={yAxisDomain}
            tickFormatter={props =>
              (unit == '$' ? exchangeRates[currency].symbol : '') +
              abbreviateNumber(props, true) +
              (unit != '$' ? unit : '')
            }
            style={{
              fontSize: '0.75rem',
              fontFamily: 'Roboto',
            }}
          />
          <Tooltip
            isAnimationActive={false}
            content={<CustomTooltip active payload label currency={currency} graphKey={graphKey} kpiData={kpiData} />}
            position={{ y: -120 }}
          />
          {isBreakevenAnalysis ? (
            <Bar
              dataKey="value"
              fill={COLOR_PRIMARY}
              barSize={13}
              unit={unit == '$' ? exchangeRates[currency].symbol : unit}
              style={{ cursor: 'pointer' }}
              radius={[4, 4, 4, 4]}
              name={legendMap['total_net_operating_income'] || 'Breakeven Value'}
            >
              {formattedData.map((entry, index) => {
                const entryValue = (entry as { name: string; value: number }).value;
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={entryValue < 0 ? '#ef5350' : entryValue === 0 ? '#ffa726' : '#66bb6a'}
                  />
                );
              })}
            </Bar>
          ) : isObject(chartData[Object.keys(chartData)[0]]) ? (
            Object.keys(chartData[Object.keys(chartData)[0]]).map((barKey, index) => {
              if (barKey === 'total_revenue') return null;
              return (
                <Bar
                  key={barKey}
                  dataKey={barKey}
                  stackId={'revenueStack'}
                  fill={barColorsMap[barKey] || COLOR_PRIMARY}
                  radius={
                    index === Object.keys(chartData).length - 2 ? [100, 100, 0, 0] : [0, 0, 0, 0]
                  }
                  barSize={13}
                  unit={unit === '$' ? exchangeRates[currency]?.symbol : unit}
                  name={legendMap[barKey] || barKey}
                  style={{ cursor: 'pointer' }}
                />
              );
            })
          ) : (
            <Bar
              dataKey="value"
              fill={COLOR_PRIMARY}
              barSize={13}
              unit={unit == '$' ? exchangeRates[currency].symbol : unit}
              style={{ cursor: 'pointer' }}
              radius={[100, 100, 0, 0]}
              name={legendMap['value'] || 'value'}
            />
          )}
          {!isBreakevenAnalysis && (
          <Legend iconType="circle" wrapperStyle={{ fontSize: '0.75rem', fontFamily: 'Roboto' }} />
          )}
        </BarChart>
      </ResponsiveContainer>

      {!isUserSubscribed && (
        <DisabledBlurCard
          subscriptionType="Expert"
          onDisabledOptionClick={() => {
            handleLoginOrPricingModalOpen('open');
          }}
          addBlur="2px"
        />
      )}
    </Box>
  );
};

const fmtCurrency = (value: number, currency: string): string => {
  const sym = exchangeRates[currency]?.symbol || '$';
  return `${sym}${abbreviateNumber(value, true)}`;
};

function BreakevenSummaryRow({
  label,
  value,
  emphasize,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  emphasize?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: '8px',
        borderBottom: '1px solid #e5e7eb',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Typography
        sx={{
          fontSize: emphasize ? '13px' : '12px',
          fontWeight: emphasize ? 600 : 500,
          color: '#374151',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: emphasize ? '14px' : '13px',
          fontWeight: 700,
          color: '#1f2937',
          flexShrink: 0,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function BreakevenDataTable({ kpiData, currency }: { kpiData: any; currency: string }) {
  const startupCosts = kpiData?.startup_costs_total || 0;
  const yearlyNetProfit = kpiData?.yearly_net_profit_before_tax || 0;
  const monthlyProfit = Math.round(yearlyNetProfit / 12);
  const monthsToBreakeven =
    monthlyProfit > 0 && startupCosts > 0
      ? Math.ceil(startupCosts / monthlyProfit)
      : null;

  const months = Array.from({ length: 24 }, (_, i) => i + 1);

  return (
    <Box sx={{ mt: '8px' }}>
      {/* Summary breakdown */}
      <Box sx={{ mb: '12px' }}>
        <Typography sx={{ fontSize: '11px', color: '#9ca3af', mb: '6px' }}>
          How long until your initial investment is recovered from monthly profits
        </Typography>
        <BreakevenSummaryRow
          label={<>Startup cost <StartupCostSetupLink /></>}
          value={startupCosts > 0 ? fmtCurrency(startupCosts, currency) : '—'}
        />
        <BreakevenSummaryRow
          label="Monthly net profit"
          value={
            <span style={{ color: monthlyProfit >= 0 ? '#059669' : '#dc2626' }}>
              {fmtCurrency(monthlyProfit, currency)}
            </span>
          }
        />
        <BreakevenSummaryRow
          label="= Estimated months to breakeven"
          value={
            monthsToBreakeven != null
              ? monthsToBreakeven <= 24
                ? `${monthsToBreakeven} months`
                : '> 24 months'
              : 'N/A'
          }
          emphasize
        />
      </Box>

      {/* Monthly table */}
      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', mb: '6px' }}>
        Monthly breakdown (24 months)
      </Typography>
      <Box
        sx={{
          overflowX: 'auto',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          '&::-webkit-scrollbar': { height: '6px' },
          '&::-webkit-scrollbar-thumb': { background: '#d1d5db', borderRadius: '3px' },
        }}
      >
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse',
            minWidth: '1400px',
            width: '100%',
            '& th, & td': {
              fontSize: '11px',
              px: '10px',
              py: '6px',
              whiteSpace: 'nowrap',
              borderBottom: '1px solid #f3f4f6',
              textAlign: 'right',
            },
            '& th': {
              fontWeight: 600,
              color: '#6b7280',
              background: '#f9fafb',
              position: 'sticky',
              top: 0,
            },
            '& th:first-of-type, & td:first-of-type': {
              textAlign: 'left',
              position: 'sticky',
              left: 0,
              background: '#fff',
              zIndex: 1,
              fontWeight: 600,
              color: '#374151',
            },
            '& th:first-of-type': { background: '#f9fafb', zIndex: 2 },
          }}
        >
          <thead>
            <tr>
              <th></th>
              {months.map(m => (
                <th key={m}>M{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Initial investment</td>
              {months.map(m => (
                <td key={m} style={{ color: '#6b7280' }}>
                  {m === 1 ? fmtCurrency(startupCosts, currency) : '–'}
                </td>
              ))}
            </tr>
            <tr>
              <td>Net profit</td>
              {months.map(m => (
                <td key={m} style={{ color: monthlyProfit >= 0 ? '#059669' : '#dc2626' }}>
                  {fmtCurrency(monthlyProfit, currency)}
                </td>
              ))}
            </tr>
            <tr>
              <td>Cumulative profit</td>
              {months.map(m => {
                const cumulative = monthlyProfit * m;
                return (
                  <td key={m} style={{ color: cumulative >= 0 ? '#059669' : '#dc2626' }}>
                    {fmtCurrency(cumulative, currency)}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td>Remaining to breakeven</td>
              {months.map(m => {
                const cumulative = monthlyProfit * m;
                const remaining = startupCosts - cumulative;
                const reachedBreakeven = remaining <= 0;
                return (
                  <td key={m} style={{ color: reachedBreakeven ? '#059669' : '#9ca3af', fontWeight: reachedBreakeven ? 600 : 400 }}>
                    {reachedBreakeven ? 'Recovered' : fmtCurrency(remaining, currency)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </Box>
      </Box>
    </Box>
  );
}

function CashFlowDataTable({ kpiData, currency }: { kpiData: any; currency: string }) {
  const cashFlowData = kpiData?.five_years_cash_flow;
  if (!cashFlowData) return null;

  const years = Object.keys(cashFlowData).sort();

  return (
    <Box sx={{ mt: '8px' }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', mb: '6px' }}>
        Yearly breakdown
      </Typography>
      <Box
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse',
            width: '100%',
            '& th, & td': {
              fontSize: '12px',
              px: '14px',
              py: '8px',
              borderBottom: '1px solid #f3f4f6',
              textAlign: 'right',
            },
            '& th': {
              fontWeight: 600,
              color: '#6b7280',
              background: '#f9fafb',
            },
            '& th:first-of-type, & td:first-of-type': { textAlign: 'left' },
            '& tr:last-of-type td': { borderBottom: 'none' },
          }}
        >
          <thead>
            <tr>
              <th></th>
              {years.map(y => (
                <th key={y}>{y.replace('Y', 'Year ')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ fontWeight: 600, color: '#374151' }}>Accumulated cash flow</td>
              {years.map(y => {
                const val = cashFlowData[y] ?? 0;
                return (
                  <td key={y} style={{ fontWeight: 600, color: val >= 0 ? '#059669' : '#dc2626' }}>
                    {fmtCurrency(val, currency)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </Box>
      </Box>
    </Box>
  );
}

const GraphSection = ({
  kpiData,
  reportType,
  currency,
  handleLoginOrPricingModalOpen,
  isUserSubscribed,
  appreciationRate = 4,
  onAppreciationChange,
}: {
  kpiData: any;
  reportType: string;
  currency: string;
  handleLoginOrPricingModalOpen: any;
  isUserSubscribed: boolean;
  appreciationRate?: number;
  onAppreciationChange?: (val: number) => void;
}) => {
  const defaultProjectionByReportTypeMap = Object.keys(futureProjections).reduce(
    (acc, graphKey) => {
      const graphConfig = futureProjections[graphKey];
      const { showAsDeafaultIn } = graphConfig;
      acc[showAsDeafaultIn] = graphKey;
      return acc;
    },
    {}
  );

  const availableGraphs = Object.keys(futureProjections).reduce((acc, graphKey) => {
    const graphConfig = futureProjections[graphKey];
    const { displayName, allowedModes } = graphConfig;
    if (allowedModes && !allowedModes.includes(reportType)) return acc;
    acc[graphKey] = displayName;
    return acc;
  }, {} as Record<string, string>);
  const graphOptionCount = Object.keys(availableGraphs).length;

  const [selectedGraph, setSelectedGraph] = useState(defaultProjectionByReportTypeMap[reportType]);
  const tooltipConfig = futureProjections[selectedGraph]?.tooltipConfig;
  const isSingleOption = graphOptionCount <= 1;

  return (
    <Box
      sx={{
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        p: '14px 16px',
        background: '#fff',
      }}
    >
      {isSingleOption ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: '4px' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f2937' }}>
            {futureProjections[selectedGraph]?.displayName || 'Projection'}
          </Typography>
          {tooltipConfig && (
            <InfoButton
              tooltipContent={
                <TooltipContent
                  tooltipConfig={tooltipConfig}
                  selectedCompsCount={null}
                  ltmDateRange={null}
                />
              }
            />
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: '12px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DropdownSelect
              name="Projection Type"
              value={selectedGraph}
              setValue={setSelectedGraph}
              options={availableGraphs}
            />
            {tooltipConfig && (
              <InfoButton
                tooltipContent={
                  <TooltipContent
                    tooltipConfig={tooltipConfig}
                    selectedCompsCount={null}
                    ltmDateRange={null}
                  />
                }
              />
            )}
          </Box>
          {selectedGraph === 'tenYearsReturnRate' && onAppreciationChange && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '180px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#4b5563', whiteSpace: 'nowrap' }}>
                Appreciation: {appreciationRate}%
              </Typography>
              <Slider
                size="small"
                value={appreciationRate}
                min={0}
                max={15}
                step={0.5}
                onChange={(_, val) => onAppreciationChange(val as number)}
                valueLabelDisplay="auto"
                valueLabelFormat={v => `${v}%`}
                sx={{
                  color: COLOR_PRIMARY,
                  width: '100px',
                  '& .MuiSlider-thumb': { width: 14, height: 14 },
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {selectedGraph === 'twentyFourMonthsBreakevenAnalysis' && (
        <BreakevenDataTable kpiData={kpiData} currency={currency} />
      )}
      {selectedGraph === 'fiveYearsCashFlow' && (
        <CashFlowDataTable kpiData={kpiData} currency={currency} />
      )}

      <Box sx={{ mt: '12px' }}>
        {renderChart(
          selectedGraph,
          kpiData,
          currency,
          handleLoginOrPricingModalOpen,
          isUserSubscribed
        )}
      </Box>
    </Box>
  );
};

export default GraphSection;
